import {Component, OnInit} from '@angular/core';
import {Request} from "../../classes/request";
import {RequestService} from "../../services/request";
import {HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {
    RequestCompleteModalComponent
} from "../../modals/request/request-complete-modal/request-complete-modal.component";
import {
    RequestUncompleteModalComponent
} from "../../modals/request/request-uncomplete-modal/request-uncomplete-modal.component";
import {RequestCreateModalComponent} from "../../modals/request/request-create-modal/request-create-modal.component";
import {RequestDeleteModalComponent} from "../../modals/request/request-delete-modal/request-delete-modal.component";
import {GeneralModalComponent} from "../../modals/general/general-modal/general-modal.component";
import {AuthService} from "../../services/auth";
import {User} from "../../classes/user";
import { EmailService } from 'src/app/services/email';
import {RequestTypeService} from "../../services/requestType";
import {RequestType} from "../../classes/requestType";
import {RequestNotesModalComponent} from "../../modals/request/request-notes-modal/request-notes-modal.component";
import {RequestAccessModalComponent} from "../../modals/request/request-access-modal/request-access-modal.component";
import {
    AdvancedSearchField,
    AdvancedSearchState,
    countActiveRules,
    emptyAdvancedState
} from "../../utils/advancedSearch";
import {loadAdvancedState, saveAdvancedState} from "../../utils/filterStateStorage";
import {csvFileName, downloadCsv, formatCsvDate} from "../../utils/csvExport";
import {
    CUP_PROBLEM_OPTIONS,
    cupProblemLabel,
    hasPhase1Problem as checkPhase1Problem,
    isDnsSabatiniViewRequest,
    participationProblemLabel as participationProblemLabelOf,
    phase1ProblemLabels as phase1ProblemLabelsOf,
    practiceFolderName as folderNameOf,
    practicePhase as resolvePracticePhase,
    practicePhaseLabel as practicePhaseLabelOf,
    PRACTICE_PHASE_FILTER_OPTIONS,
    STAMP_PROBLEM_OPTIONS,
    stampProblemLabel
} from "../../utils/dnsProblems";
import {
    IPER_PHASE_FILTER_OPTIONS,
    IPER_PROBLEM_OPTIONS,
    iperPhase as resolveIperPhase,
    iperPhaseLabelOf,
    iperProblemLabels as iperProblemLabelsOf,
    IperPhase,
    isIperViewRequest
} from "../../utils/iperProblems";
import {RequestRevenueStampModalComponent} from "../../modals/request/request-revenue-stamp-modal/request-revenue-stamp-modal.component";

type DashboardView = 'base' | 'dns' | 'iper';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
    // Pagination Parameters
    paginationConfig = {
        itemsPerPage: 25,
        currentPage: 1
    };
    columnLayoutToken = 0;
    paginationParams = {
        autoHide: true,
        maxSize: 7,
        responsive: true
    };
    paginationLabels = {
        previousLabel: 'Precedente',
        nextLabel: 'Successiva',
        screenReaderPaginationLabel: 'Paginazione',
        screenReaderPageLabel: 'pagina',
        screenReaderCurrentLabel: `Sei sulla pagina`
    };

    // Filters
    globalSearchFilterValue: string = '';
    idFilterValue: string = '';
    creationDateFromFilterValue: string = '';
    creationDateToFilterValue: string = '';
    companyNameFilterValue: string = '';
    requestTypeFilterValue: number[] = [];
    emailFilterValue: string = '';
    internalManagementFilterValue: string = '';
    mobilePhoneFilterValue: string = '';
    progressStatusFilterValue: string[] = [];
    stampProblemFilterValue: string[] = [];
    cupProblemFilterValue: string[] = [];
    practicePhaseFilterValue: string[] = [];
    notesFilterValue: string = '';
    folderNameFilterValue: string = '';
    readonly progressStatusOptions: string[] = [
        'In fase di compilazione',
        'Da convalidare',
        'Da controllare',
        'Completato'
    ];
    readonly stampProblemOptions = [...STAMP_PROBLEM_OPTIONS];
    readonly cupProblemOptions = [...CUP_PROBLEM_OPTIONS];
    readonly practicePhaseFilterOptions = [...PRACTICE_PHASE_FILTER_OPTIONS];
    iperPhaseFilterValue: string[] = [];
    iperProblemFilterValue: string[] = [];
    readonly iperPhaseFilterOptions = [...IPER_PHASE_FILTER_OPTIONS];
    readonly iperProblemOptions = [...IPER_PROBLEM_OPTIONS];

    requestTypes: Array<RequestType> = [];

    // Ricerca Avanzata
    private readonly screenKey = 'pratiche';
    advancedFields: AdvancedSearchField<Request>[] = this.buildAdvancedFields();
    advancedState: AdvancedSearchState = emptyAdvancedState();

    copyFeedbackMessage: string = '';
    copyFeedbackType: 'success' | 'danger' | '' = '';
    private copyFeedbackTimeout: ReturnType<typeof setTimeout> | null = null;

    user: User = new User();
    requests: Array<Request> = [];
    activeView: DashboardView = 'base';

    constructor(private router: Router,
                private route: ActivatedRoute,
                private authService: AuthService,
                private modalService: NgbModal,
                private requestService: RequestService,
                private requestTypeService: RequestTypeService,
                private emailService: EmailService) {
    }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(queryParams => {
            const vista = queryParams.get('vista');
            const view: DashboardView = vista === 'dns' || vista === 'iper' ? vista : 'base';
            if (this.activeView === view) {
                return;
            }
            this.activeView = view;
            this.paginationConfig.currentPage = 1;
            this.relockColumns();
        });

        this.advancedState = loadAdvancedState(this.screenKey, this.advancedFields) ?? emptyAdvancedState();
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                this.relockColumns();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });

        this.requestService.getAll()
            .subscribe({
                next: (requests: Array<Request>) => {
                    this.requests = requests;
                    this.relockColumns();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });

        this.requestTypeService.getAll()
            .subscribe({
                next: (requestTypeList: any) => {
                    this.requestTypes = requestTypeList;
                    this.advancedFields = this.buildAdvancedFields();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    pageChanged(event: any) {
        this.paginationConfig.currentPage = event;
        this.relockColumns();
    }

    setView(view: DashboardView): void {
        if (this.activeView === view) {
            return;
        }
        this.activeView = view;
        this.paginationConfig.currentPage = 1;
        this.relockColumns();
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {vista: view === 'base' ? null : view},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    /** Elenco della vista attiva. Sabatini e Iperammortamento mostrano solo le pratiche Completate dei tipi previsti. */
    get viewRequests(): Array<Request> {
        if (this.activeView === 'dns') {
            return this.requests.filter(request => isDnsSabatiniViewRequest(request));
        }
        if (this.activeView === 'iper') {
            return this.requests.filter(request => isIperViewRequest(request));
        }
        return this.requests;
    }

    practiceFolderName(request: Request): string {
        return folderNameOf(request);
    }

    stampProblemLabel(request: Request): string {
        return stampProblemLabel(request);
    }

    participationProblemLabel(request: Request): string {
        return participationProblemLabelOf(request);
    }

    phase1ProblemLabels(request: Request): string[] {
        return phase1ProblemLabelsOf(request);
    }

    hasPhase1Problem(request: Request): boolean {
        return checkPhase1Problem(request);
    }

    practicePhase(request: Request): 1 | 2 {
        return resolvePracticePhase(request);
    }

    practicePhaseLabel(request: Request): string {
        return practicePhaseLabelOf(request);
    }

    cupProblemLabel(request: Request): string {
        return cupProblemLabel(request);
    }

    iperPhase(request: Request): IperPhase {
        return resolveIperPhase(request);
    }

    iperPhaseLabel(request: Request): string {
        return iperPhaseLabelOf(request);
    }

    iperProblemLabels(request: Request): string[] {
        return iperProblemLabelsOf(request);
    }

    private relockColumns(): void {
        this.columnLayoutToken++;
    }

    onFilterChange(): void {
        this.paginationConfig.currentPage = 1;
    }

    clearFilters(): void {
        this.globalSearchFilterValue = '';
        this.idFilterValue = '';
        this.creationDateFromFilterValue = '';
        this.creationDateToFilterValue = '';
        this.companyNameFilterValue = '';
        this.requestTypeFilterValue = [];
        this.emailFilterValue = '';
        this.internalManagementFilterValue = '';
        this.mobilePhoneFilterValue = '';
        this.progressStatusFilterValue = [];
        this.stampProblemFilterValue = [];
        this.cupProblemFilterValue = [];
        this.practicePhaseFilterValue = [];
        this.notesFilterValue = '';
        this.folderNameFilterValue = '';
        this.iperPhaseFilterValue = [];
        this.iperProblemFilterValue = [];
        this.advancedState = emptyAdvancedState();
        saveAdvancedState(this.screenKey, this.advancedState, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    onAdvancedStateChange(state: AdvancedSearchState): void {
        this.advancedState = state;
        saveAdvancedState(this.screenKey, state, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    private buildAdvancedFields(): AdvancedSearchField<Request>[] {
        const yesNo = [{value: 'true', label: 'Sì'}, {value: 'false', label: 'No'}];
        // DNS e Iper 4.0 esistono solo per le pratiche di tipo 6: per le altre il valore resta vuoto.
        const completion = (request: Request, done: boolean) =>
            request.FKIDRequestType === 6 ? (done ? 'true' : 'false') : '';
        return [
            {key: 'id', label: 'ID Pratica', type: 'number', value: r => r.IDRequest},
            {key: 'creationDate', label: 'Data creazione', type: 'date', value: r => r.CreationDate},
            {key: 'companyName', label: 'Ragione sociale', type: 'text', value: r => r.customer?.CompanyName},
            {key: 'vatNumber', label: 'Partita IVA', type: 'text', value: r => r.customer?.VATNumber},
            {key: 'city', label: 'Città', type: 'text', value: r => r.customer?.City},
            {
                key: 'requestType', label: 'Tipologia', type: 'select',
                options: (this.requestTypes || [])
                    .filter(type => type.IDRequestType !== undefined)
                    .map(type => ({value: String(type.IDRequestType), label: type.RequestType})),
                value: r => r.FKIDRequestType
            },
            {key: 'email', label: 'Email', type: 'text', value: r => r.Email},
            {
                key: 'internalManagement',
                label: 'Gestione Interna',
                type: 'select',
                options: yesNo,
                value: r => r.InternalManagement ? 'true' : 'false'
            },
            {key: 'mobilePhone', label: 'Cellulare', type: 'text', value: r => r.customer?.MobilePhone},
            {
                key: 'status', label: 'Stato', type: 'select',
                options: this.progressStatusOptions.map(status => ({value: status, label: status})),
                value: r => this.resolveStatusLabel(r)
            },
            {key: 'dns', label: 'DNS completata', type: 'select', options: yesNo, value: r => completion(r, r.DNSCompleted)},
            {key: 'iper4', label: 'Iper 4.0 completato', type: 'select', options: yesNo, value: r => completion(r, r.Iper4Completed)}
        ];
    }

    /** Stessa logica delle etichette di stato mostrate in tabella. */
    private resolveStatusLabel(request: Request): string {
        if (request.ProgressStatus < 9) {
            return 'In fase di compilazione';
        }
        if (request.ProgressStatus === 9) {
            return (request.CompanyParticipation || request.PartnersParticipation) ? 'Da controllare' : 'Da convalidare';
        }
        return request.ProgressStatus === 10 ? 'Completato' : '';
    }

    exportCsv(requests: Array<Request>): void {
        if (this.activeView === 'dns') {
            this.exportDnsCsv(requests);
            return;
        }
        if (this.activeView === 'iper') {
            this.exportIperCsv(requests);
            return;
        }
        const completion = (request: Request, done: boolean) =>
            request.FKIDRequestType === 6 ? (done ? 'Sì' : 'No') : '';
        downloadCsv(
            csvFileName('pratiche'),
            ['ID Pratica', 'Data Creazione', 'Ragione Sociale', 'Tipologia', 'Email', 'Gestione Interna', 'Cellulare', 'Stato', 'DNS', 'Iper 4.0'],
            requests.map(request => [
                request.IDRequest ?? '',
                formatCsvDate(request.CreationDate),
                request.customer?.CompanyName || '',
                request.request_type?.RequestType || '',
                request.Email || '',
                request.InternalManagement ? 'Sì' : 'No',
                request.customer?.MobilePhone || '',
                this.resolveStatusLabel(request),
                completion(request, request.DNSCompleted),
                completion(request, request.Iper4Completed)
            ])
        );
    }

    private exportDnsCsv(requests: Array<Request>): void {
        downloadCsv(
            csvFileName('pratiche-sabatini'),
            [
                'ID',
                'Data Creazione',
                'Gestione Interna',
                'Ragione Sociale / Denominazione',
                'Nome cartella pratica',
                'Data Analisi',
                'Fase Pratica',
                'Problemi Sottofase 1',
                'Problemi Sottofase 2',
                'Note'
            ],
            requests.map(request => [
                request.IDRequest ?? '',
                formatCsvDate(request.CreationDate),
                request.InternalManagement ? 'Sì' : 'No',
                request.customer?.CompanyName || '',
                this.practiceFolderName(request),
                '',
                this.practicePhaseLabel(request),
                this.phase1ProblemLabels(request).join('; '),
                this.cupProblemLabel(request),
                request.Notes || ''
            ])
        );
    }

    private exportIperCsv(requests: Array<Request>): void {
        downloadCsv(
            csvFileName('pratiche-iperammortamento'),
            [
                'ID',
                'Data Creazione',
                'Gestione Interna',
                'Ragione Sociale / Denominazione',
                'Nome cartella pratica',
                'Data Analisi',
                'Fase Pratica',
                'Problemi',
                'Importo Agevolazione',
                'Note'
            ],
            requests.map(request => [
                request.IDRequest ?? '',
                formatCsvDate(request.CreationDate),
                request.InternalManagement ? 'Sì' : 'No',
                request.customer?.CompanyName || '',
                this.practiceFolderName(request),
                '',
                this.iperPhaseLabel(request),
                this.iperProblemLabels(request).join('; '),
                request.IncentivePercentageAmount || '',
                request.Notes || ''
            ])
        );
    }

    hasActiveFilters(): boolean {
        return !!(
            this.globalSearchFilterValue
            || this.idFilterValue
            || this.creationDateFromFilterValue
            || this.creationDateToFilterValue
            || this.companyNameFilterValue
            || this.requestTypeFilterValue.length > 0
            || this.emailFilterValue
            || this.internalManagementFilterValue
            || this.mobilePhoneFilterValue
            || (this.activeView === 'base' && this.progressStatusFilterValue.length > 0)
            || (this.activeView === 'dns' && (
                this.stampProblemFilterValue.length > 0
                || this.cupProblemFilterValue.length > 0
                || this.practicePhaseFilterValue.length > 0
                || this.notesFilterValue
                || this.folderNameFilterValue
            ))
            || (this.activeView === 'iper' && (
                this.iperPhaseFilterValue.length > 0
                || this.iperProblemFilterValue.length > 0
                || this.notesFilterValue
                || this.folderNameFilterValue
            ))
            || countActiveRules(this.advancedState, this.advancedFields) > 0
        );
    }

    isRequestTypeSelected(requestTypeId: number | undefined): boolean {
        return requestTypeId !== undefined && this.requestTypeFilterValue.includes(requestTypeId);
    }

    onRequestTypeToggle(requestTypeId: number | undefined, event: Event): void {
        if (requestTypeId === undefined) {
            return;
        }
        const checked = (event.target as HTMLInputElement).checked;
        const selected = new Set(this.requestTypeFilterValue);
        if (checked) {
            selected.add(requestTypeId);
        } else {
            selected.delete(requestTypeId);
        }
        this.requestTypeFilterValue = this.requestTypes
            .map(requestType => requestType.IDRequestType)
            .filter((id): id is number => id !== undefined && selected.has(id));
        this.onFilterChange();
    }

    getRequestTypeFilterLabel(): string {
        if (this.requestTypeFilterValue.length === 0) {
            return 'Tutte';
        }
        return this.requestTypes
            .filter(requestType => requestType.IDRequestType !== undefined
                && this.requestTypeFilterValue.includes(requestType.IDRequestType))
            .map(requestType => requestType.RequestType)
            .join(', ');
    }

    isProgressStatusSelected(status: string): boolean {
        return this.progressStatusFilterValue.includes(status);
    }

    onProgressStatusToggle(status: string, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        const selected = new Set(this.progressStatusFilterValue);
        if (checked) {
            selected.add(status);
        } else {
            selected.delete(status);
        }
        this.progressStatusFilterValue = this.progressStatusOptions.filter(option => selected.has(option));
        this.onFilterChange();
    }

    getProgressStatusFilterLabel(): string {
        if (this.progressStatusFilterValue.length === 0) {
            return 'Tutti';
        }
        return this.progressStatusFilterValue.join(', ');
    }

    isStampProblemSelected(option: string): boolean {
        return this.stampProblemFilterValue.includes(option);
    }

    onStampProblemToggle(option: string, event: Event): void {
        this.stampProblemFilterValue = this.toggleFilterOption(
            this.stampProblemFilterValue,
            this.stampProblemOptions,
            option,
            event
        );
    }

    getStampProblemFilterLabel(): string {
        return this.stampProblemFilterValue.length === 0 ? 'Tutti' : this.stampProblemFilterValue.join(', ');
    }

    isCupProblemSelected(option: string): boolean {
        return this.cupProblemFilterValue.includes(option);
    }

    onCupProblemToggle(option: string, event: Event): void {
        this.cupProblemFilterValue = this.toggleFilterOption(
            this.cupProblemFilterValue,
            this.cupProblemOptions,
            option,
            event
        );
    }

    getCupProblemFilterLabel(): string {
        return this.cupProblemFilterValue.length === 0 ? 'Tutti' : this.cupProblemFilterValue.join(', ');
    }

    isPracticePhaseSelected(option: string): boolean {
        return this.practicePhaseFilterValue.includes(option);
    }

    onPracticePhaseToggle(option: string, event: Event): void {
        this.practicePhaseFilterValue = this.toggleFilterOption(
            this.practicePhaseFilterValue,
            this.practicePhaseFilterOptions,
            option,
            event
        );
    }

    getPracticePhaseFilterLabel(): string {
        return this.practicePhaseFilterValue.length === 0 ? 'Tutte' : this.practicePhaseFilterValue.join(', ');
    }

    isIperPhaseSelected(option: string): boolean {
        return this.iperPhaseFilterValue.includes(option);
    }

    onIperPhaseToggle(option: string, event: Event): void {
        this.iperPhaseFilterValue = this.toggleFilterOption(
            this.iperPhaseFilterValue,
            this.iperPhaseFilterOptions,
            option,
            event
        );
    }

    getIperPhaseFilterLabel(): string {
        return this.iperPhaseFilterValue.length === 0 ? 'Tutte' : this.iperPhaseFilterValue.join(', ');
    }

    isIperProblemSelected(option: string): boolean {
        return this.iperProblemFilterValue.includes(option);
    }

    onIperProblemToggle(option: string, event: Event): void {
        this.iperProblemFilterValue = this.toggleFilterOption(
            this.iperProblemFilterValue,
            this.iperProblemOptions,
            option,
            event
        );
    }

    getIperProblemFilterLabel(): string {
        return this.iperProblemFilterValue.length === 0 ? 'Tutti' : this.iperProblemFilterValue.join(', ');
    }

    private toggleFilterOption(current: string[], options: string[], option: string, event: Event): string[] {
        const checked = (event.target as HTMLInputElement).checked;
        const selected = new Set(current);
        if (checked) {
            selected.add(option);
        } else {
            selected.delete(option);
        }
        this.onFilterChange();
        return options.filter(item => selected.has(item));
    }

    goToRequestDetail(idRequest: number) {
        this.router.navigate(['/richiesta/' + idRequest]);
    }

    openRevenueStampModal(request: Request, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const modalRef = this.modalService.open(RequestRevenueStampModalComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });
        modalRef.componentInstance.request = request;
        modalRef.result.then((updated: Request) => {
            if (updated) {
                request.RevenueStamp = updated.RevenueStamp;
            }
        }, () => {});
    }

    hasNotes(request: Request): boolean {
        return Number(request.NotesCount || 0) > 0;
    }

    openNotesModal(request: Request) {
        const modalRef = this.modalService.open(RequestNotesModalComponent, {
            size: 'xl',
            centered: true,
            windowClass: 'notes-modal-window'
        });
        modalRef.componentInstance.request = request;
    }

    openAccessesModal(request: Request) {
        const modalRef = this.modalService.open(RequestAccessModalComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });
        modalRef.componentInstance.request = request;
    }

    copyRequestLink(requestToken: string) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(window.location.origin + '/richiesta?session=' + requestToken).then(() => {
              this.showCopyFeedback('Link copiato negli appunti.', 'success');
            }).catch(err => {
              console.error('Errore nella copia', err);
              this.showCopyFeedback('Impossibile copiare il link.', 'danger');
            });
          } else {
            this.showCopyFeedback('Copia negli appunti non supportata da questo browser.', 'danger');
          }
    }

    private showCopyFeedback(message: string, type: 'success' | 'danger'): void {
        this.copyFeedbackMessage = message;
        this.copyFeedbackType = type;
        if (this.copyFeedbackTimeout) {
            clearTimeout(this.copyFeedbackTimeout);
        }
        this.copyFeedbackTimeout = setTimeout(() => {
            this.copyFeedbackMessage = '';
            this.copyFeedbackType = '';
            this.copyFeedbackTimeout = null;
        }, 2000);
    }

    openCreateRequestModal() {
        const modalRef = this.modalService.open(RequestCreateModalComponent, { size: 'xl', centered: true });
        modalRef.result.then((requestBack: Request) => {
            if (requestBack.IDRequest !== undefined) {
                this.requestService.getAll()
                    .subscribe({
                        next: (requests: Array<Request>) => {
                            this.requests = requests;
                            this.relockColumns();
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        });
    }

    openCompleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestCompleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                requestBack.ProgressStatus = 10;
                this.requestService.update(requestBack)
                    .subscribe({
                        next: () => {},
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        });
    }

    openUncompleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestUncompleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                requestBack.ProgressStatus = 9;
                this.requestService.update(requestBack)
                    .subscribe({
                        next: () => {},
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        });
    }

    openDeleteRequestModal(request: Request) {
        const modalRef = this.modalService.open(RequestDeleteModalComponent);
        modalRef.componentInstance.request = request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                this.requestService.delete(requestBack.IDRequest)
                    .subscribe({
                        next: () => {
                            this.requests = this.requests.filter((request: Request) => request.IDRequest !== requestBack.IDRequest);
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        });
    }

    openSendReminderModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Invio Sollecito';
        modalRef.componentInstance.text = 'Sei sicuro di voler inviare un sollecito?';
        modalRef.result.then(() => {
            this.emailService.sendReminder(request.IDRequest!)
                .subscribe({
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    openCompleteDnsModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Completamento DNS';
        modalRef.componentInstance.text = 'Confermi il completamento DNS per la pratica "' + (request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.DNSCompleted = true;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.DNSCompleted = false;
                        console.log(err);
                    }
                });
        });
    }

    openCompleteIper4Modal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Completamento Iperammortamento 4.0';
        modalRef.componentInstance.text = 'Confermi il completamento Iperammortamento 4.0 per la pratica "' + (request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.Iper4Completed = true;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.Iper4Completed = false;
                        console.log(err);
                    }
                });
        });
    }

    openUncompleteDnsModal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Annullamento completamento DNS';
        modalRef.componentInstance.text = 'Confermi di annullare il completamento DNS per la pratica "' + (request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.DNSCompleted = false;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.DNSCompleted = true;
                        console.log(err);
                    }
                });
        });
    }

    openUncompleteIper4Modal(request: Request) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Annullamento completamento Iperammortamento 4.0';
        modalRef.componentInstance.text = 'Confermi di annullare il completamento Iperammortamento 4.0 per la pratica "' + (request.customer?.CompanyName || '') + '"?';
        modalRef.result.then(() => {
            request.Iper4Completed = false;
            this.requestService.update(request)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        request.Iper4Completed = true;
                        console.log(err);
                    }
                });
        });
    }

}
